"use client"
import {
  ContentCategory,
  SiteContent,
  SiteContentWithCategory,
  CreateContentCategoryInput,
  UpdateContentCategoryInput,
  CreateSiteContentInput,
  UpdateSiteContentInput,
  ContentQueryInput,
  BulkUpdateContentInput,
  ContentListResponse,
  CategoryListResponse,
  ContentHistory
} from '@repo/types';
import { BaseApiService } from './base-api.service';
import { AxiosInstance } from 'axios';
import { axiosInstance } from '../axios-instance';
import { ENDPOINTS } from '../endpoint';

export class SiteContentService extends BaseApiService {
  constructor(axios: AxiosInstance = axiosInstance) {
    super(ENDPOINTS.SITE_CONTENT.BASE, axios);
  }

  // =============================================
  // PUBLIC ENDPOINTS
  // =============================================

  async getPublicContent(): Promise<Record<string, string>> {
    const response = await this.axios.get<Record<string, string>>(
      `${ENDPOINTS.SITE_CONTENT.BASE}/public`
    );
    return response.data;
  }

  // =============================================
  // CONTENT CATEGORIES
  // =============================================

  async getCategories(): Promise<CategoryListResponse> {
    const response = await this.axios.get<CategoryListResponse>(
      `${ENDPOINTS.SITE_CONTENT.BASE}/categories`
    );
    return response.data;
  }

  async getCategoryById(id: string): Promise<ContentCategory> {
    const response = await this.axios.get<ContentCategory>(
      `${ENDPOINTS.SITE_CONTENT.BASE}/categories/${id}`
    );
    return response.data;
  }

  async createCategory(data: CreateContentCategoryInput): Promise<ContentCategory> {
    const response = await this.axios.post<ContentCategory>(
      `${ENDPOINTS.SITE_CONTENT.BASE}/categories`,
      data
    );
    return response.data;
  }

  async updateCategory(id: string, data: UpdateContentCategoryInput): Promise<ContentCategory> {
    const response = await this.axios.put<ContentCategory>(
      `${ENDPOINTS.SITE_CONTENT.BASE}/categories/${id}`,
      data
    );
    return response.data;
  }

  async deleteCategory(id: string): Promise<{ success: boolean }> {
    const response = await this.axios.delete<{ success: boolean }>(
      `${ENDPOINTS.SITE_CONTENT.BASE}/categories/${id}`
    );
    return response.data;
  }

  // =============================================
  // SITE CONTENT
  // =============================================

  async getContent(query: ContentQueryInput = {} as any): Promise<ContentListResponse> {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    const response = await this.axios.get<ContentListResponse>(
      `${ENDPOINTS.SITE_CONTENT.BASE}?${params}`
    );
    return response.data;
  }

  async getContentById(id: string): Promise<SiteContentWithCategory> {
    const response = await this.axios.get<SiteContentWithCategory>(
      `${ENDPOINTS.SITE_CONTENT.BASE}/${id}`
    );
    return response.data;
  }

  async createContent(data: CreateSiteContentInput): Promise<SiteContentWithCategory> {
    const response = await this.axios.post<SiteContentWithCategory>(
      ENDPOINTS.SITE_CONTENT.BASE,
      data
    );
    return response.data;
  }

  async updateContent(id: string, data: UpdateSiteContentInput): Promise<SiteContentWithCategory> {
    const response = await this.axios.put<SiteContentWithCategory>(
      `${ENDPOINTS.SITE_CONTENT.BASE}/${id}`,
      data
    );
    return response.data;
  }

  async deleteContent(id: string): Promise<{ success: boolean }> {
    const response = await this.axios.delete<{ success: boolean }>(
      `${ENDPOINTS.SITE_CONTENT.BASE}/${id}`
    );
    return response.data;
  }

  // =============================================
  // BULK OPERATIONS
  // =============================================

  async bulkUpdateContent(data: BulkUpdateContentInput): Promise<{ count: number }> {
    const response = await this.axios.put<{ count: number }>(
      `${ENDPOINTS.SITE_CONTENT.BASE}/bulk`,
      data
    );
    return response.data;
  }

  // =============================================
  // CONTENT HISTORY
  // =============================================

  async getContentHistory(
    contentId: string,
    page = 1,
    limit = 20
  ): Promise<{
    data: ContentHistory[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    const response = await this.axios.get(
      `${ENDPOINTS.SITE_CONTENT.BASE}/${contentId}/history?page=${page}&limit=${limit}`
    );
    return response.data;
  }

  // =============================================
  // VALIDATION
  // =============================================

  async validateContent(
    contentId: string,
    value: string
  ): Promise<{ valid: boolean; errors: string[] }> {
    const response = await this.axios.post<{ valid: boolean; errors: string[] }>(
      `${ENDPOINTS.SITE_CONTENT.BASE}/validate/${contentId}`,
      { value }
    );
    return response.data;
  }

  // =============================================
  // LEGACY SUPPORT (for backwards compatibility)
  // =============================================

  /**
   * @deprecated Use getPublicContent() instead
   */
  async getAll(): Promise<SiteContent[]> {
    const response = await this.axios.get<SiteContent[]>(
      `${ENDPOINTS.SITE_CONTENT.BASE}/legacy`
    );
    return response.data;
  }
}

export const siteContentService = new SiteContentService();
